import './Reviews.scss';
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Sparkle, Star } from '../components/Icons';
import {
  GOOGLE_RATING,
  GOOGLE_REVIEW_COUNT,
  GOOGLE_REVIEWS_URL,
  GOOGLE_WRITE_REVIEW_URL,
  GoogleReview,
  googleReviews as reviews,
  initials,
  reviewMonth,
} from '../data/googleReviews';

const PER_PAGE = 3;
const pages = Math.ceil(reviews.length / PER_PAGE);
const LONG = 260;

const d = (ms: number) => ({ '--delay': `${ms}ms` } as React.CSSProperties);

const Stars = ({ n }: { n: number }) => (
  <span className="stars" aria-label={`${n} out of 5 stars`}>
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} />
    ))}
  </span>
);

/** One review card; long reviews are clamped with a Read more toggle. */
function ReviewCard({ r }: { r: GoogleReview }) {
  const [open, setOpen] = useState(false);
  const long = r.text.length > LONG;
  // Trim at a word boundary so the card never cuts a word in half.
  const shown = !long || open ? r.text : r.text.slice(0, r.text.lastIndexOf(' ', LONG)).replace(/[\s,;:.!]+$/, '') + '…';
  return (
    <article className={`review${open ? ' is-open' : ''}`}>
      <div className="review__top">
        <span className="review__rating">
          {r.stars} <Stars n={r.stars} />
        </span>
        <span className="review__where">{reviewMonth(r.date)}</span>
      </div>
      <p>{shown}</p>
      {long && (
        <button className="review__more" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? 'Show less' : 'Read more'}
        </button>
      )}
      <footer>
        <span className="review__avatar" aria-hidden="true">
          {initials(r.name)}
        </span>
        <span>{r.name}</span>
        <a className="review__g" href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" aria-label="See this review on Google">
          G
        </a>
      </footer>
    </article>
  );
}

export function Reviews() {
  const [page, setPage] = useState(0);
  const visible = reviews.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);

  return (
    <section id="reviews" className="section reviews">
      <div className="container">
        <div className="head head--center">
          <span className="eyebrow reveal">Success stories</span>
          <h2 className="reveal" style={d(80)}>
            What Our Clients Say.
          </h2>
        </div>

        <div className="widget reveal" style={d(160)}>
          <div className="widget__inner">
            <div className="widget__head">
              <h3>What our clients say about us</h3>
              <div className="widget__score">
                <div>
                  <strong>{GOOGLE_RATING.toFixed(1)}</strong>
                  <span className="widget__meta">
                    <Stars n={5} />
                    <a className="widget__count" href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer">
                      {GOOGLE_REVIEW_COUNT} Google reviews
                    </a>
                  </span>
                </div>
                <a className="btn btn--blue btn--sm" href={GOOGLE_WRITE_REVIEW_URL} target="_blank" rel="noopener noreferrer">
                  Write a review
                </a>
              </div>
            </div>

            <div className="widget__summary">
              <span>
                <Sparkle /> Summary
              </span>
              <p>
                Every one of our {GOOGLE_REVIEW_COUNT} Google reviews is five stars. Customers keep mentioning attention to
                detail, crews that leave the house and yard spotless, fair quotes with no surprise costs, and responsive
                project management from Henry and Jack from the first visit to the final walkthrough.
              </p>
            </div>

            <div className="widget__grid" key={page}>
              {visible.map((r) => (
                <ReviewCard key={r.id} r={r} />
              ))}
            </div>

            <div className="widget__pager">
              <button className="icon-btn" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} aria-label="Previous reviews">
                <ChevronLeft />
              </button>
              <span className="widget__pagenum" aria-live="polite">
                {page + 1} / {pages}
              </span>
              <div className="widget__pages">
                {Array.from({ length: pages }).map((_, i) => (
                  <button key={i} className={i === page ? 'is-active' : ''} onClick={() => setPage(i)} aria-label={`Page ${i + 1}`}>
                    {i + 1}
                  </button>
                ))}
              </div>
              <button className="icon-btn" onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page === pages - 1} aria-label="Next reviews">
                <ChevronRight />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
